/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — notes-autopopulate.ts
   Trial-balance auto-populate logic for the disclosure questionnaire.
   ═══════════════════════════════════════════════════════════════════════ */

import { state } from './state';
import { meta, rows, el, fmt, cur } from './utils';
import {
  INVENTORY_PATTERN,
  ACCUMULATED_DEPRECIATION_PATTERN,
  INTANGIBLE_PATTERN,
  BAD_DEBT_PATTERN,
  DEBT_PATTERN,
  LEASE_PATTERN,
  CASH_PATTERN,
} from './patterns';

// Track whether auto-populate has already run this session to avoid overwriting user edits
let autoPopulateRan = false;

// ─── Helpers ───────────────────────────────────────────────────────────────

function hasItemPattern(section: string, pattern: RegExp): boolean {
  return rows(state.currentData, section).some(r => pattern.test(r.label));
}

function setIfEmpty(id: string, value: string): void {
  const field = el(id) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
  if (field && !field.value.trim()) field.value = value;
}

function setRadioIfDefault(name: string, value: string): void {
  const current = document.querySelector('input[name="' + name + '"]:checked') as HTMLInputElement | null;
  // Only set if currently on 'no' (default)
  if (!current || current.value === 'no') {
    const target = document.querySelector('input[name="' + name + '"][value="' + value + '"]') as HTMLInputElement | null;
    if (target) {
      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

/**
 * Scans trial balance data to auto-populate empty questionnaire fields.
 * Only fills fields that are currently blank — never overwrites user input.
 */
export function autoPopulateFromTB(): void {
  if (autoPopulateRan) return; // Only auto-fill once per session

  // Only mark as ran if there's actual TB data to scan
  const hasAnyData = rows(state.currentData, 'current-assets').length > 0 ||
    rows(state.currentData, 'noncurrent-assets').length > 0 ||
    rows(state.currentData, 'revenue').length > 0;
  if (!hasAnyData) return; // No data yet — try again next time

  autoPopulateRan = true;

  const c = cur();

  // ── Inventory detection ──
  const invItems = rows(state.currentData, 'current-assets').filter(r => INVENTORY_PATTERN.test(r.label));
  if (invItems.length > 0) {
    // Auto-detect inventory method based on common patterns
    setIfEmpty('nq-inventory', 'FIFO');
  }

  // ── Depreciation detection ──
  const hasDepreciation = hasItemPattern('noncurrent-assets', ACCUMULATED_DEPRECIATION_PATTERN);
  if (hasDepreciation) {
    setIfEmpty('nq-depreciation', 'straight-line');
    // Build useful lives suggestion from asset types
    const assetTypes: string[] = [];
    rows(state.currentData, 'noncurrent-assets').forEach(r => {
      if (/building/i.test(r.label)) assetTypes.push('Buildings: 39 years');
      else if (/machinery|equipment/i.test(r.label) && !/computer/i.test(r.label)) assetTypes.push('Machinery & Equipment: 5-10 years');
      else if (/vehicle/i.test(r.label)) assetTypes.push('Vehicles: 5 years');
      else if (/furniture|fixture/i.test(r.label)) assetTypes.push('Furniture & Fixtures: 7 years');
      else if (/computer/i.test(r.label)) assetTypes.push('Computer Equipment: 3-5 years');
      else if (/leasehold/i.test(r.label)) assetTypes.push('Leasehold Improvements: lesser of useful life or lease term');
    });
    const unique = [...new Set(assetTypes)];
    if (unique.length > 0) setIfEmpty('nq-useful-lives', unique.join('; '));
  }

  // ── Intangible assets detection ──
  const hasIntangibles = hasItemPattern('noncurrent-assets', INTANGIBLE_PATTERN);
  if (hasIntangibles) {
    setRadioIfDefault('nq-intangibles', 'yes');
    const intTypes: string[] = [];
    rows(state.currentData, 'noncurrent-assets').forEach(r => {
      if (/goodwill/i.test(r.label)) intTypes.push('Goodwill is tested for impairment annually, or more frequently if triggering events occur, in accordance with ASC 350.');
      if (/patent/i.test(r.label)) intTypes.push('Patents are amortized on a straight-line basis over their estimated useful lives.');
      if (/trademark/i.test(r.label)) intTypes.push('Trademarks with indefinite useful lives are not amortized but are tested for impairment annually.');
      if (/customer/i.test(r.label)) intTypes.push('Customer relationships are amortized on a straight-line basis over their estimated useful lives.');
    });
    const uniqueInt = [...new Set(intTypes)];
    if (uniqueInt.length > 0) setIfEmpty('nq-intangibles-text', uniqueInt.join(' '));
  }

  // ── Allowance for doubtful accounts detection ──
  const hasAllowance = hasItemPattern('current-assets', BAD_DEBT_PATTERN);
  if (hasAllowance) {
    setRadioIfDefault('nq-allowance', 'yes');
    setIfEmpty('nq-allowance-text', 'The Company maintains an allowance for doubtful accounts based on management\'s assessment of the collectability of specific customer accounts and the aging of accounts receivable. Accounts receivable are written off when deemed uncollectible.');
  }

  // ── Debt detection ──
  const hasDebt = hasItemPattern('current-liab', DEBT_PATTERN) ||
                  hasItemPattern('noncurrent-liab', DEBT_PATTERN);
  if (hasDebt) {
    setRadioIfDefault('nq-debt', 'yes');
    // Build debt summary from account labels and amounts
    const debtLines: string[] = [];
    const allDebt = [
      ...rows(state.currentData, 'current-liab').filter(r => DEBT_PATTERN.test(r.label)),
      ...rows(state.currentData, 'noncurrent-liab').filter(r => DEBT_PATTERN.test(r.label)),
    ];
    allDebt.forEach(r => {
      const amt = Math.abs(parseFloat(String(r.amount)) || 0);
      debtLines.push(r.label + ': ' + c + fmt(amt));
    });
    if (debtLines.length > 0) {
      setIfEmpty('nq-debt-text', 'The Company has the following outstanding debt obligations: ' + debtLines.join('; ') + '.');
    }
  }

  // ── Lease detection ──
  const hasLeases = hasItemPattern('current-liab', LEASE_PATTERN) ||
                    hasItemPattern('noncurrent-liab', LEASE_PATTERN) ||
                    hasItemPattern('noncurrent-assets', LEASE_PATTERN);
  if (hasLeases) {
    setRadioIfDefault('nq-leases', 'yes');
    setIfEmpty('nq-leases-text', 'The Company has operating lease agreements for office space and equipment. Right-of-use assets and lease liabilities are recognized at the commencement date based on the present value of remaining lease payments, discounted using the Company\'s incremental borrowing rate.');
  }

  // ── Equity auto-populate ──
  const eqItems = rows(state.currentData, 'equity');
  if (eqItems.length > 0) {
    const eqLines: string[] = [];
    eqItems.forEach(r => {
      if (/common\s*stock/i.test(r.label)) {
        eqLines.push(r.label + '.');
      } else if (/treasury/i.test(r.label)) {
        const amt = Math.abs(parseFloat(String(r.amount)) || 0);
        eqLines.push('Treasury stock of ' + c + fmt(amt) + ' represents shares repurchased by the Company.');
      }
    });
    if (eqLines.length > 0) setIfEmpty('nq-equity-text', eqLines.join(' '));
  }

  // ── FDIC detection (cash > 250k) ──
  const cashItems = rows(state.currentData, 'current-assets').filter(r => CASH_PATTERN.test(r.label));
  const totalCash = cashItems.reduce((t, r) => t + Math.abs(parseFloat(String(r.amount)) || 0), 0);
  if (totalCash > 250000) {
    setRadioIfDefault('nq-fdic', 'yes');
  }

  // ── Income tax status guess ──
  if (hasItemPattern('other', /income\s*tax\s*expense/i)) {
    setIfEmpty('nq-tax-status', 'c-corp');
  }

  // ── Entity type inference ──
  const eqItemsForEntity = rows(state.currentData, 'equity');
  const hasCommonStock = eqItemsForEntity.some(r => /common\s*stock|preferred\s*stock|share/i.test(r.label));
  const hasMembersEquity = eqItemsForEntity.some(r => /member|partner|capital\s*account/i.test(r.label));
  const entityField = document.getElementById('nq-entity-type') as HTMLSelectElement | null;
  if (entityField && !entityField.value) {
    if (hasCommonStock) entityField.value = 'Corporation';
    else if (hasMembersEquity) entityField.value = 'LLC';
  }

  // ── Advertising policy ──
  if (hasItemPattern('opex', /advertising|marketing/i)) {
    const adField = document.getElementById('nq-advertising') as HTMLSelectElement | null;
    if (adField && !adField.value) adField.value = 'expensed';
  }

  // ── Revenue recognition auto-fill ──
  const revItems = rows(state.currentData, 'revenue');
  if (revItems.length > 0) {
    const revTypes = revItems.map(r => r.label).join(', ');
    setIfEmpty('nq-revenue-recognition', 'The Company recognizes revenue from ' + revTypes + ' when control of goods or services is transferred to the customer, in an amount that reflects the consideration expected in exchange, in accordance with ASC 606.');
  }

  // ── Subsequent events date pre-fill ──
  const m = meta();
  if (m.reportDate) {
    setIfEmpty('nq-subseq-date', m.reportDate);
  }

  console.log('[NoteFlow] Auto-populated notes questionnaire from trial balance data');
}
